import { useState } from 'react'
import {
  Stack,
  InputLabel,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  TextField
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { AtributosProducto, GruposList } from '../../Integraciones/types/responses'
import { Rubro } from '../../Rubros/AbmRubros/types/rubro'

export interface AtributoSeleccionado {
  slug: string
  valores: { slug: string; valor: string }[]
  vlibre: string
}

interface AtributosProps {
  rubro: Rubro
  atributos: AtributosProducto[]
  loading: boolean
  errorAtr: boolean
  seleccionados: AtributoSeleccionado[]
  setSeleccionados: React.Dispatch<React.SetStateAction<AtributoSeleccionado[]>>
}

const isColorAttr = (slug: string) => slug === 'color'

const ColorChipLabel = ({ nombre, color }: { nombre: string; color: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    {nombre}
    {color && (
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          backgroundColor: color,
          border: '1px solid rgba(0,0,0,0.2)',
          flexShrink: 0
        }}
      />
    )}
  </Box>
)

const MAX_VLIBRE_LENGTH = 75

const Atributos = ({ rubro, atributos, loading, errorAtr, seleccionados, setSeleccionados }: AtributosProps) => {
  const [atrSeleccionado, setAtrSeleccionado] = useState<AtributosProducto | null>(null)
  const [gruposDisp, setGruposDisp] = useState<GruposList[]>(null)
  const [open, setOpen] = useState(false)
  const [vTemp, setVTemp] = useState<{ slug: string; valor: string }[]>([])
  const [vLibreTemp, setVLibreTemp] = useState<string>(null)
  const permitidos = atributos.filter((a: AtributosProducto) =>
    rubro?.atributosPermitidos?.some((p) => p.slug === a.slug)
  )

  const handleOpenDialog = (a: AtributosProducto) => {
    const gruposPermitidos = rubro?.atributosPermitidos?.find((p) => p.slug === a.slug)?.gruposPermitidos ?? []
    const grupos = a.grupos.filter((g: GruposList) => gruposPermitidos.includes(g.idGrupo))
    setAtrSeleccionado(a)
    setGruposDisp(grupos)
    const existing = seleccionados.find((s) => s.slug === a.slug)
    setVTemp(existing?.valores ?? [])
    setVLibreTemp(existing?.vlibre)
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
    setAtrSeleccionado(null)
    setGruposDisp(null)
    setVTemp([])
    setVLibreTemp(null)
  }

  const handleConfirm = () => {
    if (!atrSeleccionado) return
    setSeleccionados((prev) => {
      const exists = prev.some((s) => s.slug === atrSeleccionado.slug)
      const updated: AtributoSeleccionado = {
        slug: atrSeleccionado.slug,
        valores: vTemp,
        vlibre: vLibreTemp
      }
      return exists ? prev.map((s) => (s.slug === atrSeleccionado.slug ? updated : s)) : [...prev, updated]
    })
    handleClose()
  }

  const removeValue = (slug: string, v: string, type: 'valor' | 'vlibre') => {
    setSeleccionados((prev) =>
      prev.map((s) => {
        if (s.slug !== slug) return s
        return type === 'valor' ? { ...s, valores: s.valores.filter((val) => val.valor !== v) } : { ...s, vlibre: null }
      })
    )
  }

  const handleChipClick = (slug: string, nombre: string) => {
    setVTemp((prev) => {
      const isSelected = prev.some((vt) => vt.slug === slug)
      if (isSelected) return prev.filter((vt) => vt.slug !== slug)
      if (!atrSeleccionado.admiteMuchosValores) {
        setVLibreTemp(null)
        return [{ slug, valor: nombre }]
      }
      return [...prev, { slug, valor: nombre }]
    })
  }

  const handleValorLibreChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (!atrSeleccionado.admiteMuchosValores) setVTemp([])
    setVLibreTemp(e.target.value)
  }

  return (
    <>
      <Box
        sx={{
          margin: '20px 0',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: '20px'
        }}
      >
        {permitidos.map((a: AtributosProducto) => {
          const atrActual = seleccionados.find((s: AtributoSeleccionado) => s.slug === a.slug)
          const esObligatorio = rubro?.atributosPermitidos?.find((p) => p.slug === a.slug)?.obligatorio
          const faltaValor = errorAtr && esObligatorio && !atrActual?.valores?.length && !atrActual?.vlibre

          return (
            <Stack key={a.slug} spacing={1}>
              <InputLabel>
                {a.admiteMuchosValores ? a.nombrePlural : a.nombreSingular}
              </InputLabel>
              {/* Contenedor visual */}
              <Box
                sx={{
                  minHeight: 56,
                  border: '1px solid',
                  borderColor: faltaValor ? 'error.main' : 'divider',
                  borderRadius: 1,
                  p: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5,
                  backgroundColor: 'background.paper'
                }}
              >
                {!atrActual?.valores.length && !atrActual?.vlibre ? (
                  <Typography variant="body2" color="text.disabled" sx={{ alignSelf: 'center', pl: 0.5 }}>
                    Sin valores seleccionados
                  </Typography>
                ) : (
                  <>
                    {a.grupos
                      .filter((g) =>
                        rubro?.atributosPermitidos
                          ?.find((p) => p.slug === a.slug)
                          ?.gruposPermitidos?.includes(g.idGrupo)
                      )
                      .flatMap((g) =>
                        g.valoresPredefinidos
                          .filter((vp) => atrActual?.valores.some((v) => v.slug === vp.slug))
                          .map((vp) => (
                            <Chip
                              key={vp.slug}
                              label={vp.nombre}
                              size="small"
                              color="primary"
                              onDelete={() => removeValue(a.slug, vp.nombre, 'valor')}
                            />
                          ))
                      )}
                    {atrActual?.vlibre && (
                      <Chip
                        key={`${a.slug}-vlibre`}
                        label={atrActual.vlibre}
                        size="small"
                        color="secondary"
                        onDelete={() => removeValue(a.slug, atrActual.vlibre, 'vlibre')}
                      />
                    )}
                  </>
                )}
                <Button size="small" startIcon={<AddIcon />} onClick={() => handleOpenDialog(a)} sx={{ ml: 'auto' }}>
                  Agregar
                </Button>
              </Box>
              {faltaValor && (
                <Typography variant="caption" color="error">
                  Debes agregar un valor para este atributo
                </Typography>
              )}
            </Stack>
          )
        })}
      </Box>
      {atrSeleccionado && (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
          <DialogTitle>
            {atrSeleccionado.admiteMuchosValores ? atrSeleccionado.nombrePlural : atrSeleccionado.nombreSingular}
          </DialogTitle>
          <DialogContent>
            {gruposDisp?.map((g: GruposList) => (
              <Box key={g.nombre} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {g.nombre}
                </Typography>

                {/* Chips predefinidos */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                  {g.valoresPredefinidos.map((v: any) => {
                    const selected = vTemp.some((vt) => vt.slug === v.slug)
                    const esColor = isColorAttr(atrSeleccionado.slug)
                    return (
                      <Chip
                        key={`${g.idGrupo}-${v.slug}`}
                        label={esColor ? <ColorChipLabel nombre={v.nombre} color={v.codigoColor} /> : v.nombre}
                        clickable
                        size="small"
                        color={selected ? 'primary' : 'default'}
                        variant={selected ? 'filled' : 'outlined'}
                        onClick={() => handleChipClick(v.slug, v.nombre)}
                      />
                    )
                  })}
                </Box>
              </Box>
            ))}
            {/* Valor libre */}
            {rubro?.atributosPermitidos.find((a) => a.slug === atrSeleccionado.slug)?.puedeCargarValorLibre && (
              <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Si no encontraste el valor que querías, escribilo acá
                  </Typography>

                  <Typography variant="subtitle2" fontWeight="normal">
                    {vLibreTemp?.length || 0}/{MAX_VLIBRE_LENGTH}
                  </Typography>
                </Box>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Agregar valor..."
                  value={vLibreTemp || ''}
                  onChange={handleValorLibreChange}
                  disabled={loading}
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: MAX_VLIBRE_LENGTH }}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button size="small" variant="outlined" color="error" onClick={handleClose}>
              Cancelar
            </Button>
            <Button size="small" variant="outlined" color="success" onClick={handleConfirm}>
              Confirmar
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default Atributos
